import type { AxiosProgressEvent } from 'axios';
import { api } from '../../../core/api/client';
import { filesApi } from '../../../core/api/files.api';
import { compressImageIfNeeded } from '@/shared/utils/image-compression';

/**
 * File folders enum for organized storage
 */
export enum FileFolder {
  LESSON_IMAGES = 'LESSON_IMAGES',
  SLIDE_IMAGES = 'SLIDE_IMAGES',
  OPTION_IMAGES = 'OPTION_IMAGES',
  LESSON_ANSWER_PHOTOS = 'LESSON_ANSWER_PHOTOS',
  LESSON_PDFS = 'LESSON_PDFS',
}

/**
 * API client for file uploads
 */
export class UploadAPIClient {
  /**
   * Upload image file
   * Automatically compresses images larger than 1280px.
   * `onProgress` primește 0-100 (axios onUploadProgress); opțional, pentru progres per fișier.
   */
  async uploadImage(
    file: File,
    folder: FileFolder,
    onProgress?: (pct: number) => void
  ): Promise<string> {
    const compressedFile = await compressImageIfNeeded(file);

    const formData = new FormData();
    formData.append('file', compressedFile);

    return api.post('/api/files/upload', formData, {
      params: { folder },
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (event: AxiosProgressEvent) => {
        if (!onProgress || !event.total) return;
        onProgress(Math.round((event.loaded / event.total) * 100));
      },
    });
  }

  /**
   * Upload multiple images
   */
  async uploadMultipleImages(
    files: File[],
    folder: FileFolder
  ): Promise<string[]> {
    const uploadPromises = files.map((file) =>
      this.uploadImage(file, folder)
    );
    return Promise.all(uploadPromises);
  }

  /**
   * Validate image file
   */
  validateImage(file: File): { valid: boolean; error?: string } {
    // Check file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      return {
        valid: false,
        error: 'Tipul de fișier nu este acceptat. Folosiți JPG, PNG sau WEBP.',
      };
    }

    // Check file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      return {
        valid: false,
        error: 'Fișierul este prea mare. Dimensiunea maximă este 5MB.',
      };
    }

    return { valid: true };
  }

  /**
   * Create object URL for preview
   */
  createPreviewUrl(file: File): string {
    return URL.createObjectURL(file);
  }

  /**
   * Revoke object URL to free memory
   */
  revokePreviewUrl(url: string): void {
    URL.revokeObjectURL(url);
  }

  /**
   * Validates PDF files before upload
   */
  validatePdf(file: File): { valid: boolean; error?: string } {
    // Check file type
    if (file.type !== 'application/pdf') {
      return {
        valid: false,
        error: 'Doar fișiere PDF sunt permise.',
      };
    }

    // Check file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      return {
        valid: false,
        error: 'Fișierul PDF este prea mare. Dimensiunea maximă este 10MB.',
      };
    }

    return { valid: true };
  }

  /**
   * Uploads a PDF file to Azure Blob Storage
   */
  async uploadPdf(file: File, folder: FileFolder): Promise<string> {
    try {
      const result = await filesApi.uploadFile(file, folder);
      // Backend may return either a string URL or an object with url property
      return typeof result === 'string' ? result : result.url;
    } catch (error: unknown) {
      console.error('PDF upload failed:', error);
      throw new Error('Failed to upload PDF');
    }
  }
}

// Export singleton instance
export const uploadAPI = new UploadAPIClient();